import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Helmet } from 'react-helmet-async'

export interface FAQItem {
  question: string;
  answer: string;
}

interface PageFAQProps {
  faqs: FAQItem[];
  title?: string;
  subtitle?: string;
  variant?: 'light' | 'dark' | 'muted';
  showSchema?: boolean;
  backgroundImage?: string;
}

export const PageFAQ = ({
  faqs,
  title = "Frequently Asked Questions",
  subtitle,
  variant = 'muted',
  showSchema = true,
  backgroundImage
}: PageFAQProps) => {
  // Generate JSON-LD Schema for FAQs
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const bgClasses = {
    light: 'bg-white',
    dark: 'bg-navy-900 text-white',
    muted: 'bg-slate-50'
  };

  const textClasses = {
    light: 'text-gray-900',
    dark: 'text-white',
    muted: 'text-gray-900'
  };

  const subtitleClasses = {
    light: 'text-gray-600',
    dark: 'text-gray-300',
    muted: 'text-gray-600'
  };

  const triggerClasses = {
    light: 'text-gray-900 hover:text-blue-600',
    dark: 'text-white hover:text-blue-400',
    muted: 'text-gray-900 hover:text-blue-600'
  };

  const contentClasses = {
    light: 'text-gray-600',
    dark: 'text-gray-300',
    muted: 'text-gray-600'
  };

  const borderClasses = {
    light: 'border-gray-200',
    dark: 'border-white/10',
    muted: 'border-gray-200'
  };

  const isGlassy = !!backgroundImage;
  const itemBgClass = isGlassy ? 'bg-white/60 backdrop-blur-md shadow-sm' : (variant === 'dark' ? 'bg-white/5' : 'bg-white');

  return (
    <>
      {showSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        </Helmet>
      )}
      <section className={`py-20 ${!isGlassy ? bgClasses[variant] : 'relative overflow-hidden'}`}>
        {isGlassy && (
          <>
            <div className="absolute inset-0 bg-cover bg-center opacity-100" style={{ backgroundImage: `url(${backgroundImage})` }} />
            <div className="absolute inset-0 bg-white/60" />
          </>
        )}
        <div className="section-container relative z-10">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${textClasses[variant]}`}>
              {title}
            </h2>
            {subtitle && (
              <p className={`text-lg leading-relaxed ${subtitleClasses[variant]}`}>
                {subtitle}
              </p>
            )}
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className={`border rounded-xl px-6 ${borderClasses[variant]} ${itemBgClass}`}
                >
                  <AccordionTrigger className={`text-lg font-semibold text-left py-5 ${triggerClasses[variant]}`}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className={`text-base leading-relaxed pb-5 ${contentClasses[variant]}`}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
};

export default PageFAQ;
